/**
 * LLM Scan Results Page
 *
 * Streamlined security report with:
 * - Risk Scorecard (pass rate, severity, compliance)
 * - Findings with inline remediation (expandable)
 * - All Tests table (for audit)
 */

import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { AlertCircle, Table as TableIcon, Share2 } from "lucide-react";
import { RiskScorecard } from "@/components/llm/RiskScorecard";
import { FindingsWithRemediation } from "@/components/llm/FindingsWithRemediation";
import { VulnerabilitiesTable } from "@/components/llm/VulnerabilitiesTable";
import { VulnerabilityDetailModal } from "@/components/llm/VulnerabilityDetailModal";
import { ShareScanModal } from "@/components/llm/ShareScanModal";
import { useLLMScanSummary, useLLMScanResults } from "@/hooks/useLLMScans";
import { useAnalytics } from "@/hooks/useAnalytics";
import { classifyIssues, calculateVerdict } from "@/utils/issueClassification";
import type { LLMScanResult } from "@/types/llm";

export function LLMScanResultsPage() {
  useApi();
  const { track } = useAnalytics();

  const { scanUuid } = useParams<{ scanUuid: string }>();
  const [selectedResult, setSelectedResult] = useState<LLMScanResult | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const tableItemsPerPage = 10;

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useLLMScanSummary(scanUuid || "");

  const {
    data: resultsData,
    isLoading: resultsLoading,
    error: resultsError,
  } = useLLMScanResults(scanUuid || "", { page: 1, items_per_page: 1000 }); // Fetch all for table pagination

  // Get results early (empty array if not loaded yet)
  const results = resultsData?.data || [];

  // Classify issues and calculate verdict - MUST be called before any early returns
  const classifiedIssues = useMemo(() => classifyIssues(results), [results]);
  const verdict = useMemo(() => calculateVerdict(results), [results]);
  const blockingIssues = useMemo(
    () => classifiedIssues.filter((r) => r.category === "blocking" && r.status === "FAIL"),
    [classifiedIssues]
  );
  const advisoryIssues = useMemo(
    () => classifiedIssues.filter((r) => r.category === "advisory" && r.status === "FAIL"),
    [classifiedIssues]
  );

  if (!scanUuid) {
    return (
      <div className="container mx-auto p-4 sm:p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <div className="text-sm sm:text-base">Invalid scan UUID</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleResultClick = (result: LLMScanResult) => {
    setSelectedResult(result);
    setIsModalOpen(true);
  };

  if (summaryError || resultsError) {
    return (
      <div className="container mx-auto p-4 sm:p-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <div>
                <div className="font-medium text-sm sm:text-base">Error loading scan results</div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {summaryError?.message || resultsError?.message || "Unknown error"}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (summaryLoading || !summary) {
    return (
      <div className="container mx-auto p-4 sm:p-6 space-y-6">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-6">
      {/* Header with Share Button */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">AI Security Review</h1>
        <Button
          onClick={() => {
            if (scanUuid) {
              track({
                name: "scan_results_shared",
                properties: { scan_uuid: scanUuid },
              });
            }
            setIsShareModalOpen(true);
          }}
          variant="outline"
        >
          <Share2 className="h-4 w-4 mr-2" />
          Share Report
        </Button>
      </div>

      <RiskScorecard summary={summary} results={results} isLoading={summaryLoading} verdict={verdict} />

          <FindingsWithRemediation
            results={results}
            isLoading={resultsLoading}
            blockingIssues={blockingIssues}
            advisoryIssues={advisoryIssues}
          />

          {/* All Tests Table - For audit/verification */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <TableIcon className="h-4 w-4" />
                Audit Trail ({summary.scan.total_tests})
              </CardTitle>
          <p className="text-xs text-muted-foreground">Complete test results for audit and verification</p>
            </CardHeader>
            <CardContent>
              <VulnerabilitiesTable
                results={results}
                isLoading={resultsLoading}
                onResultClick={handleResultClick}
                page={tablePage}
                itemsPerPage={tableItemsPerPage}
                onPageChange={setTablePage}
              />
            </CardContent>
          </Card>

      {/* Detail Modal - Still available for full details from table */}
      <VulnerabilityDetailModal
        result={selectedResult}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
      />

      {/* Share Modal */}
      {scanUuid && (
        <ShareScanModal
          scanUuid={scanUuid}
          open={isShareModalOpen}
          onOpenChange={setIsShareModalOpen}
        />
      )}
    </div>
  );
}
